/**
 * The Display panel (canvas frame 2, display-options P2) — where the whole view is set:
 * numbered grouping levels with drag grips (nesting order is the user's to change),
 * per-level group order, sort-within-groups, filters, "Save as view". Opens anchored
 * under the Display bar's Group chip; entity-agnostic — the spec supplies the axes.
 *
 * View settings only — nothing here ever writes to a record.
 *
 * 0.1.455 (maintainer, 3 Sep 2026), two things this panel got wrong for a day of real use:
 *  · its mini-menus (axis / order / "+ Add level" / sort) closed ONLY from their own button —
 *    "very annoying". They dismiss on a press anywhere else now, and on Escape, through the
 *    same hook the panel itself uses (`useDismiss`, capture-phase so Escape closes the menu
 *    before the panel);
 *  · dragging a level reordered the STATE on every midline crossing, so rows swapped with no
 *    motion — "they just switch without any visual feedback, which looks quite weird". The
 *    state is untouched until the drop now: the grabbed row follows the pointer, lifted, and
 *    the rows it passes slide out of its way (`transform`, 120 ms), which is the feedback.
 */
import { useRef, useState, type ReactNode } from 'react';
import { MAX_LEVELS, type DisplayConfig, type EntityDisplaySpec, type GroupAxis } from './engine.js';
import { displayEdit, modeLabel, orderChoices } from './display-edit.js';
import { useDismiss } from './use-dismiss.js';

const Grip = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <circle cx="5" cy="3.5" r="1.1" fill="currentColor" />
    <circle cx="9" cy="3.5" r="1.1" fill="currentColor" />
    <circle cx="5" cy="7" r="1.1" fill="currentColor" />
    <circle cx="9" cy="7" r="1.1" fill="currentColor" />
    <circle cx="5" cy="10.5" r="1.1" fill="currentColor" />
    <circle cx="9" cy="10.5" r="1.1" fill="currentColor" />
  </svg>
);

type Picking = { level: number; what: 'axis' | 'order' } | 'add' | 'sort' | null;

/** A drag in flight: which row was grabbed, which slot it is over, how far it has moved. */
interface Drag {
  from: number;
  over: number;
  dy: number;
  /** The grabbed row's height — the distance the passed rows slide. */
  h: number;
}

export function DisplayPanel<Row>({
  spec,
  config,
  onChange,
  filters,
  onSaveView,
  maxLevels = MAX_LEVELS,
}: {
  spec: EntityDisplaySpec<Row>;
  config: DisplayConfig;
  onChange: (next: DisplayConfig) => void;
  /** The screen's filter chips / "+ Add filter" row (frame 2's Filters section). */
  filters?: ReactNode;
  /** "Save as view…" — the screen decides what it does (a status line until P7). */
  onSaveView?: () => void;
  /** The level cap for this surface (the desk's 3; the phone sheet passes its own 2). */
  maxLevels?: number;
}): JSX.Element {
  /** Which level row has its axis/order mini-menu open. */
  const [picking, setPicking] = useState<Picking>(null);
  const listRef = useRef<HTMLDivElement>(null);
  /** The row that owns the open mini-menu — a press outside it closes the menu (0.1.455). The
   *  ref lands on THAT row, so a press on the row's other button simply switches menus. */
  const menuRowRef = useDismiss(picking !== null, () => setPicking(null), { capture: true });

  // THE EDITS ARE SHARED with the phone Display sheet (display-edit.ts) — one set of rules
  // for add/remove/move/re-mode; this file keeps only the popover's markup and drag.
  const edit = displayEdit(spec, config, onChange, maxLevels);
  const { freeAxes, sortAxis, setLevels, axisById, moveLevel } = edit;

  const isLevelPick = (p: Picking, i: number, what: 'axis' | 'order'): boolean =>
    p !== null && p !== 'add' && p !== 'sort' && p.level === i && p.what === what;

  /**
   * Drag-to-reorder the levels — hand-rolled pointer tracking, no library (the bundle
   * ceiling). Nothing is committed until the drop: `drag` is paint state only, and ≤3 rows
   * keeps every step trivially cheap. Keyboard users get the same result from the axis
   * mini-menu's "Move up/down" rows below.
   */
  const [drag, setDrag] = useState<Drag | null>(null);
  const onGripDown = (index: number) => (e: React.PointerEvent) => {
    e.preventDefault();
    const list = listRef.current;
    if (list === null) return;
    const rows = Array.from(list.querySelectorAll<HTMLElement>('[data-level-row]'));
    const grabbed = rows[index];
    if (grabbed === undefined) return;
    setPicking(null);
    const startY = e.clientY;
    const h = grabbed.getBoundingClientRect().height;
    // Each row's midline BEFORE anything moves — the slots the grabbed row can land in.
    const mids = rows.map((r) => {
      const b = r.getBoundingClientRect();
      return b.top + b.height / 2;
    });
    const startMid = mids[index] ?? 0;
    let current: Drag = { from: index, over: index, dy: 0, h };
    setDrag(current);
    document.body.classList.add('is-dragging-level');

    const stop = (): void => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      document.removeEventListener('pointercancel', cancel);
      document.removeEventListener('keydown', key, { capture: true });
      document.body.classList.remove('is-dragging-level');
      setDrag(null);
    };
    const move = (ev: PointerEvent): void => {
      const dy = ev.clientY - startY;
      const centre = startMid + dy;
      // The slot whose original midline is nearest the grabbed row's centre.
      let over = index;
      let best = Number.POSITIVE_INFINITY;
      mids.forEach((mid, i) => {
        const d = Math.abs(mid - centre);
        if (d < best) {
          best = d;
          over = i;
        }
      });
      current = { from: index, over, dy, h };
      setDrag(current);
    };
    const up = (): void => {
      const { from, over } = current;
      stop();
      if (over === from) return;
      const next = [...config.levels];
      const [moved] = next.splice(from, 1);
      if (moved === undefined) return;
      next.splice(over, 0, moved);
      setLevels(next);
    };
    const cancel = (): void => stop();
    const key = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') {
        ev.stopPropagation();
        stop();
      }
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
    document.addEventListener('pointercancel', cancel);
    document.addEventListener('keydown', key, { capture: true });
  };

  /** Where row `i` paints while a drag is in flight: the grabbed row under the pointer, the
   *  rows between its origin and its slot shifted one row out of the way. */
  const dragStyle = (i: number): React.CSSProperties | undefined => {
    if (drag === null) return undefined;
    if (i === drag.from) return { transform: `translateY(${String(drag.dy)}px)` };
    if (drag.from < drag.over && i > drag.from && i <= drag.over) return { transform: `translateY(${String(-drag.h)}px)` };
    if (drag.over < drag.from && i >= drag.over && i < drag.from) return { transform: `translateY(${String(drag.h)}px)` };
    return { transform: 'translateY(0)' };
  };

  return (
    <div className="display-panel" role="dialog" aria-label="Display">
      <p className="display-panel-head">Group by</p>

      <div ref={listRef} className={drag !== null ? 'display-panel-levels display-panel-levels-dragging' : 'display-panel-levels'}>
        {config.levels.map((level, i) => {
          const axis = axisById(level.axis);
          const ownsMenu = isLevelPick(picking, i, 'axis') || isLevelPick(picking, i, 'order');
          return (
            <div
              key={level.axis}
              className={
                drag !== null && drag.from === i
                  ? 'display-panel-row display-panel-row-dragging'
                  : drag !== null
                    ? 'display-panel-row display-panel-row-shift'
                    : 'display-panel-row'
              }
              style={dragStyle(i)}
              data-level-row
              ref={ownsMenu ? menuRowRef : undefined}
            >
              <span
                className="display-panel-grip"
                aria-hidden="true"
                onPointerDown={onGripDown(i)}
              >
                <Grip />
              </span>
              <span className="display-panel-n">{i + 1}</span>
              <button
                type="button"
                className="display-panel-axis"
                aria-haspopup="menu"
                aria-expanded={isLevelPick(picking, i, 'axis')}
                onClick={() => setPicking(isLevelPick(picking, i, 'axis') ? null : { level: i, what: 'axis' })}
              >
                {axis?.label ?? level.axis}
              </button>
              <button
                type="button"
                className="display-panel-order"
                aria-haspopup="menu"
                aria-expanded={isLevelPick(picking, i, 'order')}
                onClick={() => setPicking(isLevelPick(picking, i, 'order') ? null : { level: i, what: 'order' })}
              >
                {modeLabel(level.orderMode, level.dir)}
              </button>
              {isLevelPick(picking, i, 'axis') && (
                <div className="display-menu display-panel-menu" role="menu" aria-label={`Level ${String(i + 1)}`}>
                  {[axis, ...freeAxes].filter((a): a is GroupAxis<Row> => a !== undefined).map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      role="menuitemradio"
                      aria-checked={a.id === level.axis}
                      className="display-menu-row"
                      onClick={() => {
                        setPicking(null);
                        edit.setAxis(i, a.id);
                      }}
                    >
                      {a.label}
                    </button>
                  ))}
                  {i > 0 && (
                    <button type="button" role="menuitem" className="display-menu-row display-menu-dir" onClick={() => { setPicking(null); moveLevel(i, -1); }}>
                      Move up
                    </button>
                  )}
                  {i < config.levels.length - 1 && (
                    <button type="button" role="menuitem" className="display-menu-row display-menu-dir" onClick={() => { setPicking(null); moveLevel(i, 1); }}>
                      Move down
                    </button>
                  )}
                  <button
                    type="button"
                    role="menuitem"
                    className="display-menu-row display-menu-dir"
                    onClick={() => {
                      setPicking(null);
                      edit.removeLevel(i);
                    }}
                  >
                    Remove level
                  </button>
                </div>
              )}
              {isLevelPick(picking, i, 'order') && (
                <div className="display-menu display-panel-menu display-panel-menu-order" role="menu" aria-label={`Level ${String(i + 1)} order`}>
                  {orderChoices(axis).map(({ mode, dir }) => (
                    <button
                      key={`${mode}-${dir}`}
                      type="button"
                      role="menuitemradio"
                      aria-checked={level.orderMode === mode && level.dir === dir}
                      className="display-menu-row"
                      onClick={() => {
                        setPicking(null);
                        edit.setOrder(i, mode, dir);
                      }}
                    >
                      {modeLabel(mode, dir)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {edit.canAdd && (
        <div className="display-panel-row display-panel-add-row" ref={picking === 'add' ? menuRowRef : undefined}>
          <button
            type="button"
            className="display-bar-act display-bar-act-accent"
            aria-haspopup="menu"
            aria-expanded={picking === 'add'}
            onClick={() => setPicking(picking === 'add' ? null : 'add')}
          >
            + Level
          </button>
          <span className="display-panel-note">up to {maxLevels}</span>
          {picking === 'add' && (
            <div className="display-menu display-panel-menu" role="menu" aria-label="Add level">
              {freeAxes.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  role="menuitem"
                  className="display-menu-row"
                  onClick={() => {
                    setPicking(null);
                    edit.addLevel(a.id);
                  }}
                >
                  {a.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <p className="display-panel-head">Sort within groups</p>
      <div className="display-panel-row" ref={picking === 'sort' ? menuRowRef : undefined}>
        {/* "None" until somebody picks (engine.ts `sortChosen`) — the same word the bar's chip
            shows; the menu below is the door either way. */}
        <span className="display-panel-axis display-panel-static">
          {config.sortChosen === true ? (sortAxis?.label ?? config.sortAxis) : 'None'}
        </span>
        <button
          type="button"
          className="display-panel-order display-panel-order-on"
          aria-haspopup="menu"
          aria-expanded={picking === 'sort'}
          onClick={() => setPicking(picking === 'sort' ? null : 'sort')}
        >
          {config.sortChosen !== true ? 'Choose…' : config.sortDir === 'asc' ? 'Ascending ↑' : 'Descending ↓'}
        </button>
        {picking === 'sort' && (
          <div className="display-menu display-panel-menu display-panel-menu-order" role="menu" aria-label="Sort within groups">
            {spec.sortAxes.flatMap((a) =>
              (['asc', 'desc'] as const).map((dir) => (
                <button
                  key={`${a.id}-${dir}`}
                  type="button"
                  role="menuitemradio"
                  aria-checked={config.sortChosen === true && config.sortAxis === a.id && config.sortDir === dir}
                  className="display-menu-row"
                  onClick={() => {
                    setPicking(null);
                    edit.setSort(a.id, dir);
                  }}
                >
                  {a.label} {dir === 'asc' ? '↑' : '↓'}
                </button>
              )),
            )}
          </div>
        )}
      </div>

      <p className="display-panel-head">Filters</p>
      <div className="display-panel-row">{filters}</div>

      <div className="display-panel-foot">
        {onSaveView !== undefined && (
          <button type="button" className="display-bar-act display-bar-act-accent" onClick={onSaveView}>
            Save as view…
          </button>
        )}
        <span className="display-panel-note">View settings only — nothing changes on the records.</span>
      </div>
    </div>
  );
}
